// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/budgetMath.ts
// 🔧 PURPOSE: I conti del budget in un posto solo. Prima "quanto posso
//   spendere al giorno" era scritto in due punti con due formule diverse
//   (una divideva per i giorni del soggiorno, l'altra per quelli passati):
//   l'ospite leggeva due cifre e non sapeva a quale credere. Qui c'è UNA
//   definizione, usata dal Portafoglio, da "Il mio giorno" e da Esplora.
//
//   Qui vivono anche le chiavi dei dati: sono per prenotazione, così i
//   soggiorni non si mescolano fra loro e chi legge da fuori (Esplora)
//   sa dove guardare senza copiare la regola.
// ============================================

/** Il minimo che serve ai conti: budget e spese portano sempre anche gli euro. */
export interface BudgetLike {
  amountEur: number;
  startDate?: string;
  endDate?: string;
}

export interface ExpenseLike {
  amountEur: number;
  /** Giorno della spesa, "YYYY-MM-DD". */
  date: string;
}

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Totale speso, in euro. */
export function totalSpent(expenses: ExpenseLike[] | null | undefined): number {
  if (!Array.isArray(expenses)) return 0;
  return expenses.reduce((sum, e) => sum + num(e?.amountEur), 0);
}

/** Speso in un giorno preciso ("YYYY-MM-DD"), in euro. */
export function spentOn(expenses: ExpenseLike[] | null | undefined, dayKey: string): number {
  if (!Array.isArray(expenses) || !dayKey) return 0;
  return expenses.filter((e) => String(e?.date || '').slice(0, 10) === dayKey).reduce((sum, e) => sum + num(e?.amountEur), 0);
}

/** Quanto resta del budget (può essere negativo: si è speso di più). */
export function remaining(budget: BudgetLike | null | undefined, expenses: ExpenseLike[] | null | undefined): number {
  if (!budget) return 0;
  return num(budget.amountEur) - totalSpent(expenses);
}

/**
 * Quanto si può spendere da domani in poi. `daysLeft` comprende oggi: con
 * un giorno solo restituisce tutto quello che resta, con zero giorni niente.
 */
export function perDayLeft(remainingEur: number, daysLeft: number): number {
  if (!Number.isFinite(remainingEur) || remainingEur <= 0) return 0;
  const days = Math.max(0, Math.floor(daysLeft));
  if (days <= 0) return 0;
  return remainingEur / days;
}

/** "YYYY-MM-DD" → mezzanotte locale (null se la stringa non è una data). */
function parseDay(day?: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * Giorni di budget ancora davanti, oggi compreso. Senza data di fine il
 * budget non ha una durata: torna 0 e chi chiama non mostra "al giorno".
 */
export function daysLeftOfBudget(budget: BudgetLike | null | undefined, todayKey: string): number {
  const end = parseDay(budget?.endDate);
  const start = parseDay(budget?.startDate);
  const today = parseDay(todayKey);
  if (!end || !today) return 0;
  // Prima dell'inizio si contano tutti i giorni del budget, non solo quelli da oggi.
  const from = start && start.getTime() > today.getTime() ? start : today;
  const diff = Math.floor((end.getTime() - from.getTime()) / 86_400_000) + 1;
  return diff > 0 ? diff : 0;
}

// --- Dove stanno i dati -------------------------------------------------

const SESSION_KEY = 'nva_guest_session';

/** `guest` finché non si entra: i dati scritti da sloggati non vanno persi, restano lì. */
export function bookingRef(bookingReference?: string | null): string {
  const ref = String(bookingReference || '').trim();
  return ref ? ref.replace(/\s+/g, '') : 'guest';
}

/** Come sopra ma leggendo la sessione dal locale, per chi non ha lo hook (Esplora). */
export function storedBookingRef(): string {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return 'guest';
    const session = JSON.parse(raw) as { booking?: { bookingReference?: string } };
    return bookingRef(session?.booking?.bookingReference);
  } catch {
    return 'guest';
  }
}

export const walletStorageKey = (ref: string): string => `nva-wallet:${ref}`;
export const plannerStorageKey = (ref: string): string => `nva-planner:${ref}`;

/** Le chiavi di prima della riscrittura: si leggono ancora, non si scrivono più. */
export const LEGACY_WALLET_KEY = 'travel-wallet-data';
export const LEGACY_PLANNER_KEY = 'nva-vacation-planner';

// ---- Migrazione dai vecchi salvataggi: una volta sola per strumento ----
// Senza questo marcatore la copia ripartirebbe per ogni nuova chiave (prima
// `guest`, poi la prenotazione, poi quella dell'anno dopo): le attivita' e le
// spese di un soggiorno finirebbero dentro il successivo.
const LEGACY_MARKER_PREFIX = 'nva-legacy-migrated:';

export function legacyMigrationDone(tool: 'wallet' | 'planner'): boolean {
  try {
    return window.localStorage.getItem(LEGACY_MARKER_PREFIX + tool) === '1';
  } catch {
    return true; // storage inaccessibile: niente da migrare comunque
  }
}

export function markLegacyMigrationDone(tool: 'wallet' | 'planner'): void {
  try {
    window.localStorage.setItem(LEGACY_MARKER_PREFIX + tool, '1');
  } catch {
    /* niente */
  }
}
